import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flag } from "lucide-react";
import authAxios from "../../../authAxios";
import { Alert, Button, Dialog, Field, Select, Textarea } from "../../../pages/manage/ui";

export function ReportUserDialog({ open, onClose, userId, userName, onSuccess }) {
  const { t } = useTranslation();
  const [reportReason, setReportReason] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Shown in the dialog: it also opens from pages without the kit toaster.
  const [submitError, setSubmitError] = useState("");

  const reportReasons = [
    { value: "harassment", label: t("harassment"), description: t("harassment_desc") },
    { value: "hate_speech", label: t("hate_speech"), description: t("hate_speech_desc") },
    { value: "sexual_content", label: t("sexual_content"), description: t("sexual_content_desc") },
    { value: "spam", label: t("spam"), description: t("spam_desc") },
    { value: "violence", label: t("violence"), description: t("violence_desc") },
    { value: "illegal_activity", label: t("illegal_activity"), description: t("illegal_activity_desc") },
    { value: "impersonation", label: t("impersonation"), description: t("impersonation_desc") },
    { value: "privacy_violation", label: t("privacy_violation"), description: t("privacy_violation_desc") },
    { value: "other", label: t("other"), description: t("other_desc") },
  ];

  const selectedReason = reportReasons.find((r) => r.value === reportReason);

  const handleClose = () => {
    setReportReason("");
    setReportDescription("");
    setSubmitError("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!reportReason) return;
    try {
      setIsSubmitting(true);
      setSubmitError("");
      await authAxios.post("/v1/moderation/reportContent", {
        reported_user_id: userId,
        content_type: "user_profile",
        content_id: userId,
        reason: reportReason,
        description: reportDescription.trim() || undefined,
      });

      onSuccess?.(t("report_submitted"));
      handleClose();
    } catch (error) {
      console.error("Error submitting report:", error);
      setSubmitError(t("report_submit_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!isSubmitting ? handleClose : undefined}
      hideClose={isSubmitting}
      closeOnBackdrop={false}
      size="md"
      title={userName ? t("account_report_name", { name: userName, defaultValue: "Report {{name}}" }) : t("account_report_user", { defaultValue: "Report user" })}
      description={t("report_user_info")}
      footer={
        <>
          <Button variant="light" onClick={handleClose} disabled={isSubmitting}>
            {t("cancel")}
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={!reportReason} loading={isSubmitting}>
            {!isSubmitting && <Flag />}
            {isSubmitting ? t("submitting") : t("account_submit_report", { defaultValue: "Submit report" })}
          </Button>
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Field label={t("reason_for_report")} hint={selectedReason?.description}>
          <Select value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
            <option value="" disabled>
              {t("select_reason")}
            </option>
            {reportReasons.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("additional_details")} hint={`${reportDescription.length}/500`}>
          <Textarea
            value={reportDescription}
            onChange={(e) => setReportDescription(e.target.value)}
            placeholder={t("report_details_placeholder")}
            rows={4}
            maxLength={500}
          />
        </Field>
        {submitError && <Alert variant="destructive">{submitError}</Alert>}
      </div>
    </Dialog>
  );
}
